// ============================================================
// calendarDarkPref.ts
// Per-user, per-device preference: whether the Calendar/Diary renders
// in DARK FOCUS MODE. Frontend-only, exactly like the zoom pref and the
// app theme pref — it lives in localStorage and is backed by a tiny
// standalone zustand store so the toggle button and the calendar shell
// both react to changes live (a plain localStorage read wouldn't
// re-render on toggle).
//
// This is SCOPED to the calendar: it applies a `dark` class to the
// calendar shell only, so the app's global light/dark theme is left
// untouched. (Tailwind's darkMode:'class' matches the nearest `.dark`
// ancestor, so wrapping the calendar re-themes every casevo-* token
// inside it via the CSS-var swap in index.css.)
// ============================================================
import { create } from 'zustand'

const STORAGE_KEY = 'casevo_calendar_dark'

const readDark = (): boolean => {
  try {
    return localStorage.getItem(STORAGE_KEY) === '1'
  } catch {
    return false
  }
}

interface CalendarDarkState {
  dark: boolean
  setDark: (v: boolean) => void
  toggle: () => void
}

export const useCalendarDarkPref = create<CalendarDarkState>((set, get) => ({
  dark: readDark(),
  setDark: (v: boolean) => {
    try {
      localStorage.setItem(STORAGE_KEY, v ? '1' : '0')
    } catch {
      /* storage blocked (private mode) — keep the in-memory value */
    }
    set({ dark: v })
  },
  toggle: () => get().setDark(!get().dark),
}))
